import React, { Fragment } from 'react';
import { BaseColumn, BaseColumns, FilterParams } from 'types/table';
import type { ColumnDef, Row } from '@tanstack/react-table';
import {
  CustomField,
  CustomFieldFilterOption,
  customFieldType,
  customFieldTypeToColumnType,
  PurchaseOrderData,
  ItemData,
  PoProcessType,
  StyleData,
  SubsupplierDetailUICapabilities,
  SubsupplierDetails,
  MAPPED_BY_DESIGNER,
} from '../types';
import { apiMeta } from 'api/meta';
import { FilterResponse } from 'types';
import { parseNumber } from 'table/components/filter/transforms';
import { CustomDropDown } from 'table/components/CustomDropDown';
import { t, Trans } from '@lingui/macro';

import { FacilitySubsupplierDisplay } from '../components/FacilitySubsupplierDisplay';
import { Box, Divider, type SxProps, type Theme as MuiTheme } from '@mui/material';
import { styledTheme } from 'styles/theme/theme';
import type { Theme } from '@emotion/react';
import { Plus } from '@phosphor-icons/react/dist/ssr';
import { ComboBoxOption } from 'components/inputs/types';
import { Factory } from '@phosphor-icons/react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { Avatar } from 'components/avatar/Avatar';
import { AvatarGroup } from 'components/avatar/AvatarGroup';
import { Text } from 'components/text/Text';

const getCustomFieldKey = (id: number, type: number) => `cf_${id}_${customFieldType[type]}`;

export const formatCustomFieldsToTableHeader = (
  customFields: CustomField[]
): ColumnDef<StyleData>[] =>
  (customFields || []).map(field => {
    const key = getCustomFieldKey(field.id as number, field.type as number);
    const getValue = (row: StyleData) =>
      row.customFields
        ?.filter(f => f.id === field.id)
        ?.map(item => item?.value)
        ?.join(',');
    return {
      accessorKey: 'cf',
      id: key,
      label: field.name,
      header: field.name,
      tooltipRender: ({ row }) => getValue(row.original),
      cell: ({ row }) => getValue(row.original),
      ...(field?.type === 0 ? { enableSorting: false } : {}),
    };
  });

export const formatCustomFieldToFilters = (fields: CustomField[]): BaseColumns =>
  (fields || []).reduce<BaseColumns>((acc, field) => {
    const key = getCustomFieldKey(field.id as number, field.type as number);
    const type = customFieldTypeToColumnType[field.type as number] as BaseColumn['type'];

    const baseField: BaseColumn = {
      id: key,
      label: field.name as string,
      type,
    };

    if (type === 'dd') {
      baseField.api = {
        url: apiMeta.get.filtersStyles,
        key: 'metaFiltersPurchaseOrderStep2Table',
        revalidate: true,
        responseTransform: (data: unknown) => {
          const response = (data as FilterResponse).data as unknown as CustomFieldFilterOption[];
          const options =
            response?.find(
              (item: CustomFieldFilterOption) =>
                item.customFieldForFilter?.customFieldId === field.id
            )?.options ?? [];

          return options.map(option => ({
            ...option,
            id: option.id as string,
            label: option.displayName as string,
          }));
        },
      };
    }

    acc[key] = baseField;
    return acc;
  }, {});

export const customFieldTableHeadersToFilterParams = (
  headers: ColumnDef<StyleData>[] = []
): FilterParams =>
  headers?.map(header => {
    const id = header?.id;
    const suffix = id?.split('_')?.at(-1);

    if (suffix === customFieldType[4] && id) {
      return [
        id,
        id,
        (state: Record<string, string>) => {
          const value = state?.[id];
          const [fromRaw, toRaw] = value?.split('_') ?? [];
          const from = parseNumber(fromRaw);
          const to = parseNumber(toRaw);

          return JSON.stringify({
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
          });
        },
      ];
    }

    return id;
  }) as FilterParams;

export const getApprovalStatusColor = (stateName?: string) => {
  switch (stateName?.toLowerCase()) {
    case 'in draft':
    case 'on hold':
    case 'postate_in_draft':
    case 'postate_on_hold':
    case 'exclude':
      return 'grey';

    case 'needsreview':
    case 'needs review':
    case 'needs-review':
    case 'postate_needs_review':
      return 'red';

    case 'requested':
    case 'partially approved':
    case 'partially-approved':
    case 'initiated':
    case 'partiallyapproved':
    case 'postate_initiated':
    case 'postate_requested':
    case 'partially-included':
    case 'postate_partially_approved':
      return 'blue';

    case 'awaiting approval':
    case 'awaiting review':
    case 'postate_awaiting_approval':
    case 'postate_awaiting_review':
    case 'awaitingapproval':
      return 'yellow';

    case 'postate_done':
    case 'postate_approved':
    case 'done':
    case 'approved':
    case 'include':
      return 'green';

    default:
      return 'grey';
  }
};

export enum PoMappingRequestTabs {
  Pending = 'Pending',
  ReviewAndApprove = 'ReviewAndApprove',
  AwaitingResponse = 'AwaitingResponse',
  Done = 'Done',
}

export const PoMappingRequestTabsEventMap = {
  [PoMappingRequestTabs.Pending]: 'pending_response',
  [PoMappingRequestTabs.ReviewAndApprove]: 'review_and_approve',
  [PoMappingRequestTabs.AwaitingResponse]: 'needs_review',
  [PoMappingRequestTabs.Done]: 'approved',
};

/** PO `localeCode` values where Map suppliers opens on Map styles when the URL has no tab. */
export const DEFAULT_TO_STYLES_MAP_TAB_STATUSES: readonly string[] = [
  'postate_needs_review',
  'postate_initiated',
];

export const cancelTooltip = (rows: PurchaseOrderData[]): string => {
  const isPublished = rows?.some(
    row =>
      row?.status?.visibilityStatus?.toLowerCase() !== 'unpublished' &&
      row?.status?.visibilityStatus?.toLowerCase() !== 'on hold'
  );
  if (rows.length === 1 && isPublished) {
    return t`To cancel this request, you must first set it to 'On hold'.`;
  }
  if (rows.length > 1 && isPublished) {
    return t`To cancel these requests, you must first set them to 'On hold'.`;
  }
  return '';
};

interface FacilityInfoExtract {
  id: string;
  label: string;
  supplierName: string;
  supplierAvatarColor: string;
  CustomElement?: ComboBoxOption['CustomElement'];
  customProps?: ComboBoxOption['customProps'];
  isMappedByDesigner?: boolean;
  status?: string;
  supplierId?: string;
}

function extractFacilityInfo(data?: PoProcessType): FacilityInfoExtract[] {
  return (
    data?.subsupplierDetails
      ?.filter(sub => sub?.name !== MAPPED_BY_DESIGNER)
      ?.flatMap(
        sub =>
          sub?.facilityDetails?.map(facility => ({
            id: String(facility.id),
            label: facility.name,
            supplierName: sub.name,
            supplierId: sub?.organizationSubSupplierId || sub.id,
            supplierAvatarColor: sub.avatarColor,
            status: facility.status,
            CustomElement: OptionDisplay as unknown as ComboBoxOption['CustomElement'],
            customProps: {
              id: String(facility.id),
              label: facility.name,
              supplierName: sub.name,
              supplierId: sub.id,
              supplierAvatarColor: sub.avatarColor,
              status: facility.status,
              uiCapabilities: sub.uiCapabilities,
            },
          })) ?? []
      ) ?? []
  );
}

/** Assigned row data may omit avatarColor; copy from process-type dropdown options. */
const enrichAssignedFacilityValues = (
  assigned: Partial<FacilityInfoExtract>[],
  options?: Partial<FacilityInfoExtract>[]
): Partial<FacilityInfoExtract>[] => {
  if (!assigned?.length || !options?.length) return assigned;
  return assigned.map(entry => {
    if (entry.isMappedByDesigner || entry.supplierAvatarColor) return entry;
    const match = options.find(opt => String(opt.id) === String(entry.id));
    if (!match?.supplierAvatarColor) return entry;
    return {
      ...entry,
      supplierAvatarColor: match.supplierAvatarColor,
      supplierName: entry.supplierName || match.supplierName,
      label: entry.label || match.label,
    };
  });
};

function renderFacilityOption(selected: Partial<FacilityInfoExtract>[]) {
  return function facilityOptionRenderer(option?: ComboBoxOption) {
    const matched = selected.find(item => String(item.id) === String(option?.id));
    if (matched) {
      return (
        <OptionDisplay
          id={matched.id ?? ''}
          label={matched.label ?? ''}
          supplierName={matched.supplierName ?? ''}
          supplierAvatarColor={matched.supplierAvatarColor ?? ''}
        />
      );
    }
    const props = option?.customProps ?? {};
    return (
      <OptionDisplay
        id={option?.id != null ? String(option.id) : ''}
        label={option?.label ?? ''}
        supplierName={typeof props.supplierName === 'string' ? props.supplierName : ''}
        supplierAvatarColor={
          typeof props.supplierAvatarColor === 'string' ? props.supplierAvatarColor : ''
        }
      />
    );
  };
}

const extractDesignerSupplierDetails = (data?: PoProcessType): FacilityInfoExtract[] => {
  return (
    data?.subsupplierDetails
      ?.filter(item => item?.name === MAPPED_BY_DESIGNER)
      ?.slice(0, 1)
      ?.map(sub => ({
        id: String(sub?.organizationSubSupplierId),
        label: '',
        supplierName: sub.name,
        supplierId: sub?.id,
        supplierAvatarColor: sub?.avatarColor,
        isMappedByDesigner: true,
        status: sub?.status,
        CustomElement: OptionDisplay as unknown as ComboBoxOption['CustomElement'],
        customProps: {
          id: String(sub?.organizationSubSupplierId),
          label: '',
          supplierName: sub?.name,
          supplierId: sub?.id,
          supplierAvatarColor: sub?.avatarColor,
          isMappedByDesigner: true,
          status: sub?.status,
        },
      })) ?? []
  );
};

const OptionDisplay = ({ label, supplierAvatarColor, supplierName }: FacilityInfoExtract) => (
  <FacilitySubsupplierDisplay
    facilityName={label}
    subsupplierName={supplierName}
    subsupplierAvatarColor={supplierAvatarColor}
  />
);

export const formatPoProcessTypesToFilter = (processTypes?: PoProcessType[]): BaseColumns => {
  return (
    processTypes?.reduce((prev, curr) => {
      const id = `processType_${curr?.id}`;
      return {
        ...prev,
        [id]: {
          id,
          label: curr?.name,
          type: 'dd',
          options: extractFacilityInfo(curr) as unknown as ComboBoxOption[],
        },
      };
    }, {}) ?? {}
  );
};

const commonAvatarRender = (
  first: Partial<FacilityInfoExtract>[],
  second: Partial<FacilityInfoExtract>[] | null = null,
  more: number = 0
) => {
  return [
    ...(first ?? []).map(item => (
      <Avatar
        key={item?.id}
        color={
          item?.isMappedByDesigner
            ? '#bdbdbd'
            : item?.supplierAvatarColor
              ? item.supplierAvatarColor.includes('#')
                ? item.supplierAvatarColor
                : `#${item.supplierAvatarColor}`
              : undefined
        }
        size="xsl"
        {...(!item?.isMappedByDesigner && { letters: item?.supplierName })}
        {...(item?.isMappedByDesigner && { src: '/broken-image.jpg' })}
        tooltipTitle={<AvatarTooltip {...item} />}
      />
    )),

    ...(more > 0
      ? [
          <Avatar
            key="more"
            size="xsl"
            letters={`+ ${more}`}
            tooltipTitle={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {second?.map((val, i) => (
                  <Fragment key={val?.id}>
                    {i !== 0 && (
                      <Divider
                        sx={{
                          borderColor: styledTheme.palette.border.primarySolidAlt,
                          borderWidth: '2px',
                        }}
                      />
                    )}
                    <AvatarTooltip {...val} />
                  </Fragment>
                ))}
              </Box>
            }
          />,
        ]
      : []),
  ];
};

const sortFacilityValuesBySupplierName = (
  value?: Partial<FacilityInfoExtract>[]
): Partial<FacilityInfoExtract>[] | undefined => {
  if (!value?.length) return value;
  return [...value].sort((a, b) =>
    String(b.supplierName ?? '').localeCompare(String(a.supplierName ?? ''), undefined, {
      sensitivity: 'base',
    })
  );
};

const renderValue = (value?: Partial<FacilityInfoExtract>[], isSupplier?: boolean) => {
  const sortedValue = sortFacilityValuesBySupplierName(value);
  const nonDesignerSupplier = sortedValue?.filter(item => !item?.isMappedByDesigner) ?? [];
  const designerSupplier = sortedValue?.filter(item => item?.isMappedByDesigner) ?? [];

  // Process non-designer suppliers
  const first = nonDesignerSupplier.slice(0, 4) ?? [];
  const second = nonDesignerSupplier.slice(4) ?? [];
  const more = second?.length;

  return (
    <AvatarGroup max={100} overlap="small">
      {designerSupplier?.length > 0 && isSupplier && commonAvatarRender(designerSupplier)}
      {commonAvatarRender(first, second, more)}
    </AvatarGroup>
  );
};

const readFacilityOptionStatus = (option?: ComboBoxOption): string | undefined => {
  if (typeof option?.customProps?.status === 'string') {
    return option.customProps.status;
  }
  if (option == null) return undefined;
  const status = Object.getOwnPropertyDescriptor(option, 'status')?.value;
  return typeof status === 'string' ? status : undefined;
};

const getFacilityMenuItemSx = (
  rawValues: Partial<FacilityInfoExtract>[],
  option?: ComboBoxOption
): SxProps<MuiTheme> => {
  if (rawValues.find(v => String(v.id) === String(option?.id))?.isMappedByDesigner) {
    return { display: 'none' };
  }

  if (isFacilityInvalidOption(option, rawValues)) {
    return {
      '&.Mui-disabled': { opacity: '0.7' },
    };
  }

  return readFacilityOptionStatus(option) === 'NeedsReview'
    ? { border: `0.5px solid ${styledTheme.palette.border.error}` }
    : {};
};

const AvatarTooltip = ({ label, supplierName }: Partial<FacilityInfoExtract>) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {label ? (
        <>
          <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <Factory size={16} color={styledTheme.palette.fg.white} />
            <Text variant="bodyXs" color="primaryOnBrand">
              {label}
            </Text>
          </Box>
          {supplierName && (
            <>
              <Divider
                sx={{ borderColor: styledTheme.palette.border.primarySolidAlt }}
                variant="middle"
                flexItem
              />
              <Text variant="bodyXs" color="primaryOnBrand">
                {supplierName}
              </Text>
            </>
          )}
        </>
      ) : (
        <Text variant="bodyXs" color="primaryOnBrand">
          {supplierName}
        </Text>
      )}
    </Box>
  );
};

const NoOptionsText = styled(Text)`
  text-wrap: auto;
`;

const NavLink = styled(Link)`
  color: ${p => p.theme.palette.text.link};
  font-size: ${p => p.theme.typography.labelSm};
  font-weight: 600;
  cursor: pointer;
  :hover {
    background-color: transparent;
  }
`;

const noOptionsText = (
  <NoOptionsText variant="labelSm" color="secondary">
    <Trans>No relevant suppliers, create or update suppliers</Trans>{' '}
    <NavLink to="/suppliers">
      <Trans>here</Trans>
    </NavLink>
  </NoOptionsText>
);
export const formatPoProcessTypesToTableHeaderForItems = (
  processTypes?: PoProcessType[],
  disabled?: boolean,
  isSupplier?: boolean
): ColumnDef<ItemData>[] => {
  return (
    processTypes?.map(item => {
      return {
        accessorKey: 'subSupplierFacilityDetailsForItems',
        id: String(item?.id),
        label: item?.name,
        header: item?.name,
        cell: ({ row, table }) => {
          const subsupplierFacilityDetail = row?.original?.subsupplierFacilityDetailsList?.find(
            subsupplierFacility => subsupplierFacility.id === item?.id
          );
          const facilityDetailsInfo = extractFacilityInfo(subsupplierFacilityDetail);
          const subSupplierInfo = extractDesignerSupplierDetails(subsupplierFacilityDetail) ?? [];
          const rawValues = [...facilityDetailsInfo, ...subSupplierInfo];
          const isNeedsReview = rawValues?.some(i => i.status === 'NeedsReview');
          const options = extractFacilityInfo(item)?.map(i => {
            const status = rawValues?.find(j => j?.id === i?.id && j?.supplierId === i?.supplierId)
              ?.status;
            return { ...i, status };
          });
          const displayValues = enrichAssignedFacilityValues(rawValues, options);

          return (
            <CustomDropDown
              triggerClassName="actions-button"
              noOptionsText={noOptionsText}
              value={displayValues}
              disabled={disabled}
              triggerEndIcon={() => <Plus size={20} />}
              options={options}
              renderOption={renderFacilityOption(rawValues)}
              isDisabledOption={option =>
                isFacilityInvalidOption(option as ComboBoxOption, rawValues)
              }
              placeholder={t`Assign supplier`}
              renderValue={value =>
                renderValue(value as Partial<FacilityInfoExtract>[] | undefined, isSupplier)
              }
              triggerSx={() => {
                return isNeedsReview
                  ? {
                      borderColor: styledTheme?.palette.border.error,
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: styledTheme?.palette.border.errorSolid,
                      },
                    }
                  : {};
              }}
              menuItemSx={option => getFacilityMenuItemSx(rawValues, option)}
              onChange={(event, value, reason, changedValues) => {
                table.options.meta?.updateData(row.index, {
                  reason,
                  facilityId: String(item?.id),
                  item: row?.original,
                  value: {
                    facilityProcessId: changedValues?.[0]?.customProps?.id,
                    parent: changedValues?.[0]?.customProps?.supplierId,
                  },
                });
              }}
            />
          );
        },
        minSize: 304,
        enableResizing: false,
        enableSorting: false,
      };
    }) ?? []
  );
};

export const formatPoProcessTypesToTableHeaderForStyles = (
  processTypes?: PoProcessType[],
  disabled?: boolean,
  isSupplier?: boolean
): ColumnDef<StyleData>[] => {
  return (
    processTypes?.map(item => {
      return {
        accessorKey: 'subSupplierFacilityDetailsForStyles',
        id: String(item?.id),
        label: item?.name,
        header: item?.name,
        cell: ({ row, table }) => {
          const subsupplierFacilityDetail = row?.original?.subsupplierFacilityDetailsList?.find(
            subsupplierFacility => subsupplierFacility.id === item?.id
          );
          const subsupplierFacilityDetailForChild = {
            ...(subsupplierFacilityDetail ?? {}),
            subsupplierDetails:
              subsupplierFacilityDetail?.subsupplierDetails?.filter(
                subsupplierDetail => subsupplierDetail?.poVariantId === row?.original?.poVariantId
              ) ?? [],
          } as PoProcessType;
          const subSupplierFacilityDetailsData =
            (row?.original?.childs?.length ?? 0) > 1
              ? subsupplierFacilityDetail
              : subsupplierFacilityDetailForChild;

          const facilityDetailsInfo = extractFacilityInfo(subSupplierFacilityDetailsData);

          const subSupplierInfo =
            extractDesignerSupplierDetails(subSupplierFacilityDetailsData) ?? [];
          const rawValues = [...facilityDetailsInfo, ...subSupplierInfo];
          const values = Array.from(new Map(rawValues.map(value => [value.id, value]))).map(
            ([_, value]) => value
          );
          const isNeedsReview = rawValues?.some(i => i.status === 'NeedsReview');
          const options = extractFacilityInfo(item)?.map(i => {
            const status = values?.find(j => j?.id === i?.id && j?.supplierId === i?.supplierId)
              ?.status;
            return { ...i, status };
          });
          const displayValues = enrichAssignedFacilityValues(values, options);

          return (
            <CustomDropDown
              triggerClassName="actions-button"
              noOptionsText={noOptionsText}
              value={displayValues}
              renderValue={value =>
                renderValue(value as Partial<FacilityInfoExtract>[] | undefined, isSupplier)
              }
              disabled={disabled}
              triggerEndIcon={() => <Plus size={20} />}
              options={options}
              renderOption={renderFacilityOption(values)}
              isDisabledOption={option => isFacilityInvalidOption(option as ComboBoxOption, values)}
              placeholder={t`Assign supplier`}
              triggerSx={() => {
                return isNeedsReview
                  ? {
                      borderColor: styledTheme?.palette.border.error,
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: styledTheme?.palette.border.errorSolid,
                      },
                    }
                  : {};
              }}
              menuItemSx={option => getFacilityMenuItemSx(rawValues, option)}
              onChange={(event, value, reason, changedValues) => {
                table.options.meta?.updateData(row.index, {
                  reason,
                  facilityId: String(item?.id),
                  item: row?.original,
                  value: {
                    facilityProcessId: changedValues?.[0]?.customProps?.id,
                    parent: changedValues?.[0]?.customProps?.supplierId,
                  },
                });
              }}
            />
          );
        },
        minSize: 304,
        enableResizing: false,
        enableSorting: false,
      };
    }) ?? []
  );
};

export const hasChangesDisabled = (uiCapabilities?: unknown): boolean =>
  Array.isArray(uiCapabilities) && uiCapabilities.includes('changes_disabled');

export const getUICapabilities = (uiCapabilities?: SubsupplierDetailUICapabilities[]) => {
  const check = (
    enable: SubsupplierDetailUICapabilities,
    disable: SubsupplierDetailUICapabilities
  ) => {
    if (!uiCapabilities) return true;
    if (uiCapabilities.includes(enable)) return true;
    if (uiCapabilities.includes(disable)) return false;
    return true;
  };

  return {
    isSupplierCanView: check('supplier_can_view', 'supplier_cannot_view'),
    isMappingEnabled: check('mapping_enabled', 'mapping_disabled'),
    isDocumentUploadEnabled: check('document_upload_enabled', 'document_upload_disabled'),
    isChangesEnabled: check('changes_enabled', 'changes_disabled'),
  };
};

const isFacilityInvalidOption = (
  option: Partial<ComboBoxOption> | undefined,
  selectedValues: Partial<FacilityInfoExtract>[]
) => {
  const selected = selectedValues.find(item => String(item.id) === String(option?.id));
  return Boolean(selected && hasChangesDisabled(selected.customProps?.uiCapabilities));
};

export const itemStatus: Record<string, Record<string, string>> = {
  active: { label: t`Active`, color: styledTheme.palette.success?.main || '#4caf50' },
  inactive: { label: t`Inactive`, color: styledTheme.palette.error?.main || '#f44336' },
};

/**
 * Maps purchase order status locale codes to translated text
 */
export const getPOStatusText = (localeCode: string | undefined): string => {
  if (!localeCode) return '';

  const statusMap: Record<string, string> = {
    postate_in_draft: t`In draft`,
    postate_initiated: t`Initiated`,
    postate_needs_review: t`Needs review`,
    postate_awaiting_approval: t`Awaiting approval`,
    postate_awaiting_review: t`Awaiting review`,
    postate_requested: t`Requested`,
    postate_partially_approved: t`Partially approved`,
    postate_on_hold: t`On hold`,
    postate_done: t`Done`,
    postate_approved: t`Approved`,
    postate_cancelled: t`Cancelled`,
  };

  return statusMap[localeCode.toLowerCase()] || localeCode;
};

export const getApprovalStatusText = (approvalStatus: string | undefined): string => {
  if (!approvalStatus) return '';

  const statusMap: Record<string, string> = {
    approved: t`Approved`,
    'awaiting approval': t`Awaiting approval`,
    'in review': t`In review`,
    rejected: t`Rejected`,
  };

  return statusMap[approvalStatus.toLowerCase()] || approvalStatus;
};

export const getColorFromName = (colorName: string, theme: Theme) => {
  const colorMap: Record<string, string> = {
    grey: theme.palette.border.secondary || '',
    blue: theme.palette.fg.infoPrimary || '',
    yellow: theme.palette.fg.warningPrimary || '',
    green: theme.palette.fg.successPrimary || '',
    red: theme.palette.fg.errorSecondary || '',
  };
  return colorMap[colorName] || theme.palette.border.secondary || '';
};

export const removeDuplicateMappedByDesigner = (data: SubsupplierDetails[]) => {
  let isMappedByDesigner = false;

  return data?.filter(
    i => i?.name !== MAPPED_BY_DESIGNER || !(isMappedByDesigner || !(isMappedByDesigner = true))
  );
};
